/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Tooltip } from "react-tooltip";

import { TIME_CONSTANTS } from "@constants/Bookings";
import { CONSTANTS } from "@constants/index";
import { AddToCalendarProps } from "@models/shared/shared.model";
import API_URL from "@services/apiConfig/api.config";
import { applyLocalization } from "@services/common.service";
import { RootState } from "@store/index";
import { isMobile } from "@utils/common.utils";

import "add-to-calendar-button/styles/round";
import "add-to-calendar-button/i18n/de";
import "add-to-calendar-button/i18n/fr";
import "add-to-calendar-button/i18n/it";
import "add-to-calendar-button";

const AddToCalendar: React.FC<AddToCalendarProps> = ({
  customClass,
  data,
  disabled = false,
  showTextLabel = false,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const TOOLTIP_Z_INDEX = 1000;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const handleOpen = () => setIsDropdownOpen(true);
    const handleClose = () => setIsDropdownOpen(false);

    el.addEventListener("atcb-open", handleOpen);
    el.addEventListener("atcb-close", handleClose);

    return () => {
      el.removeEventListener("atcb-open", handleOpen);
      el.removeEventListener("atcb-close", handleClose);
    };
  }, []);

  const venueName = applyLocalization(data?.club?.title) || "";
  const courtName = applyLocalization(data?.resource?.title) || "";
  const venueAddress = applyLocalization(data?.location?.title) || "";
  const latitude = data?.location?.map?.latitude;
  const longitude = data?.location?.map?.longitude;
  const url = `${window.location.origin}${API_URL.BOOKING.BOOKING_SHARE}/${data?.bookingId}`;

  const selectedLanguage = useSelector(
    (state: RootState) => state.user.selectedLanguage
  );
  const languageCode = (selectedLanguage?.split("-")[0]?.toLowerCase() ||
    "en") as "en" | "de" | "fr" | "it";

  const dateStr = moment(
    data?.startDate,
    CONSTANTS.DATE_FORMATS.YEAR_MONTH_DAY_HYPHEN_FORMAT
  ).format(CONSTANTS.DATE_FORMATS.DAY_MONTH_YEAR_FORMAT);
  const timeStr = `${data.startTime}–${data.endTime}`;

  const summary = `${t("shareTextCourtBooking")} — ${venueName}`;
  const description = `${venueName}, ${courtName} — ${dateStr}, ${timeStr}\n${url}`;

  let eventLocation = "";
  if (venueAddress) {
    eventLocation = venueAddress;
  } else if (typeof latitude === "number" && typeof longitude === "number") {
    eventLocation = `${latitude},${longitude}`;
  }

  const uid = `${data.bookingId}@availio.ch`;
  const timeZone =
    (data as { timeZone?: string }).timeZone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone ||
    "UTC";

  const customLabels = {
    apple: t("calendarLabels.apple"),
    google: t("calendarLabels.google"),
    ical: t("calendarLabels.ical"),
    outlookcom: t("calendarLabels.outlook"),
  };

  const calendarTooltipId = `fg-calendar-booking-tooltip-${data?.bookingId}`;

  const cleanStartTime =
    data.startTime === TIME_CONSTANTS.INVALID_END
      ? TIME_CONSTANTS.END_OF_DAY
      : data.startTime;
  const cleanEndTime =
    data.endTime === TIME_CONSTANTS.INVALID_END
      ? TIME_CONSTANTS.END_OF_DAY
      : data.endTime;

  return (
    <div
      ref={containerRef}
      className={`${customClass || "fg-add-to-calendar-container"} ${disabled ? "disabled" : ""} ${isDropdownOpen ? "dropdown-open" : ""}`}
      data-testid="add-to-calendar-container"
      onClick={e => e.stopPropagation()}
      data-tooltip-id={
        !disabled && !isMobile() && !showTextLabel
          ? calendarTooltipId
          : undefined
      }
      data-tooltip-position-strategy="fixed"
    >
      <add-to-calendar-button
        name={summary}
        label={t("addToCalendar")}
        options="'apple','google','outlookcom','ical'"
        location={eventLocation}
        startDate={data.startDate}
        endDate={data.endDate || data.startDate}
        startTime={cleanStartTime}
        endTime={cleanEndTime}
        description={description}
        language={languageCode}
        hideTextLabelButton={!showTextLabel}
        hideBranding={true}
        lightMode="light"
        trigger="click"
        customLabels={JSON.stringify(customLabels)}
        hideBackground
        size="2"
        disabled={disabled}
        uid={uid}
        timeZone={timeZone}
        buttonStyle="round"
        iCalFileName={`availio-booking-${data?.bookingId}`}
        hideCheckmark
      />
      {!disabled && !isMobile() && !showTextLabel && (
        <Tooltip
          id={calendarTooltipId}
          place="left"
          closeEvents={{ blur: true, click: true, mouseleave: true }}
          style={{
            zIndex: TOOLTIP_Z_INDEX,
          }}
          className="fg-tooltip-container fg-z-index-1"
        >
          {t("addToCalendar")}
        </Tooltip>
      )}
    </div>
  );
};

export default AddToCalendar;
